/**
 * Format date to readable string
 */
export function formatDate(date, locale = 'en-US') {
  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format file size to human-readable string
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Validate email format
 */
export function isValidEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text, maxLength = 50) {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

/**
 * Converts a DRF throttle detail string like
 * "Request was throttled. Expected available in 44 seconds."
 * into a user-friendly message.
 */
export function parseThrottleMessage(detail?: string): string {
  const match = detail?.match(/(\d+)\s+second/)
  const seconds = match ? parseInt(match[1], 10) : NaN
  if (!isNaN(seconds) && seconds > 0) {
    if (seconds < 60) {
      return `Too many attempts. Please wait ${seconds} seconds and try again.`
    }
    const minutes = Math.ceil(seconds / 60)
    return `Too many attempts. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} and try again.`
  }
  return 'Too many attempts. Please wait a moment and try again.'
}

/**
 * Extracts the wait-seconds from a DRF throttle detail string.
 * Returns 60 as a safe default when the value cannot be parsed.
 */
export function parseThrottleSeconds(detail?: string): number {
  const match = detail?.match(/(\d+)\s+second/)
  return match ? parseInt(match[1], 10) : 60
}

/**
 * Debounce function calls
 */
export function debounce(func, wait = 300) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Get error message from API response
 */
export function getErrorMessage(error) {
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  if (error.response?.data?.detail) {
    return error.response.data.detail
  }
  if (error.message) {
    return error.message
  }
  return 'An unexpected error occurred'
}

export function formatDuration(value: string): string {
    const trimmed = value.trim();
    const num = Number(trimmed);
    if (!isNaN(num) && trimmed !== "") {
        return num === 1 ? `${num} Year` : `${num} Years`;
    }
    return trimmed;
}
